import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import BreadcrumbListSchema from '@/components/base/BreadcrumbListSchema';
import WebPageSchema from '@/components/base/WebPageSchema';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find(c => c.slug === slug);

  if (!category) {
    return <Navigate to="/" replace />;
  }

  const categoryArticles = getArticlesByCategory(slug || '');

  return (
    <>
      <WebPageSchema
        url={`https://thevertex.com/category/${slug}`}
        name={`${category.name} — The Vertex`}
        description={category.description}
        dateModified="2026-07-07"
      />
      <BreadcrumbListSchema
        items={[
          { name: 'Home', url: 'https://thevertex.com' },
          { name: category.name, url: `https://thevertex.com/category/${slug}` },
        ]}
      />
      <main className="min-h-screen bg-background-50 font-body">
        <Navbar />
        <section className="pt-20 md:pt-24 pb-14 md:pb-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-8 md:mb-12">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-secondary-100 text-secondary-700">
                  <i className={`${category.icon} text-lg`}></i>
                </span>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">
                  {category.name}
                </h1>
              </div>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl">
                {category.description}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-10 md:mb-12 pb-4 border-b border-background-200 overflow-x-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-600 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                  <span className={`ml-1.5 text-xs ${
                    cat.slug === slug ? 'text-background-50/70' : 'text-foreground-400'
                  }`}>
                    {cat.count}
                  </span>
                </Link>
              ))}
            </div>

            {categoryArticles.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-foreground-400 text-lg">No articles in this category yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-lg border border-background-200 overflow-hidden cursor-pointer hover:border-background-300 transition-colors"
                  >
                    <div className="relative h-48 md:h-56 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        title={`${article.title} - The Vertex`}
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 leading-snug mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-xs md:text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-2.5 text-xs text-foreground-400">
                        <span className="font-medium text-foreground-600">{article.author}</span>
                        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                        <span>{article.date}</span>
                        <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                        <span>{article.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
        <Footer />
      </main>
    </>
  );
}